<template>
  <section v-if="!erro && !carregando" class="valora-cards-section mb-5">
    <div class="valora-cards-grid">
      <article
        v-for="card in cardsFormatados"
        :key="card.id"
        class="card card-custom valora-highlight-card"
      >
        <div class="card-body">
          <div class="valora-highlight-card__main">
            <div
              class="valora-highlight-card__icon"
              :style="{ color: card.cor, backgroundColor: card.fundo }"
            >
              <i :class="['mdi', card.icone]"></i>
            </div>

            <div class="valora-highlight-card__content">
              <span class="valora-highlight-card__label">{{
                card.titulo
              }}</span>
              <span
                v-if="carregando"
                class="valora-highlight-card__skeleton"
              ></span>
              <strong v-else class="valora-highlight-card__value">
                {{ card.valorFormatado }}
              </strong>
            </div>
          </div>

          <div class="valora-highlight-card__footer">
            <span
              v-if="!carregando"
              :class="['valora-delta', classeDelta(card.deltaPct)]"
            >
              <i :class="iconeDelta(card.deltaPct)"></i>
              {{ formatarDelta(card.deltaPct) }}
            </span>
            <span v-else class="valora-delta valora-delta--loading"
              >&nbsp;</span
            >
            <span class="valora-highlight-card__comparison"
              >vs período anterior</span
            >
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
const CARD_DEFINITIONS = [
  {
    id: "conversas_automatizadas",
    titulo: "Conversas Automatizadas",
    icone: "mdi-message-text-outline",
    cor: "#13a96b",
    fundo: "#e8f8f1",
    formato: "inteiro"
  },
  {
    id: "economia_estimada",
    titulo: "Economia Estimada",
    icone: "mdi-cash-multiple",
    cor: "#5d20a5",
    fundo: "#f1eafb",
    formato: "moeda"
  },
  {
    id: "tempo_poupado",
    titulo: "Tempo Poupado",
    icone: "mdi-clock-outline",
    cor: "#176bd1",
    fundo: "#eaf2fd",
    formato: "tempo"
  },
  {
    id: "conversao_atendimento",
    titulo: "Conversão em Atendimento",
    icone: "mdi-chart-line-variant",
    cor: "#ff7900",
    fundo: "#fff1e5",
    formato: "percentual"
  },
  {
    id: "agendamento_automatico",
    titulo: "Agendamento Automático",
    icone: "mdi-target",
    cor: "#e51d2a",
    fundo: "#fdebed",
    formato: "percentual"
  }
];

export default {
  name: "ValoraCards",
  props: {
    cards: {
      type: Object,
      default: () => ({})
    },
    carregando: {
      type: Boolean,
      default: false
    },
    erro: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardsFormatados() {
      return CARD_DEFINITIONS.map((definition) => {
        const dados = this.cards[definition.id] || {};
        return {
          ...definition,
          deltaPct: dados.delta_pct,
          valorFormatado: this.formatarValor(dados.valor, definition.formato)
        };
      });
    }
  },
  methods: {
    formatarValor(valor, formato) {
      const numero = Number(valor) || 0;

      if (formato === "moeda") {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL"
        }).format(numero);
      }

      if (formato === "percentual") {
        return `${numero.toLocaleString("pt-BR", {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2
        })}%`;
      }

      if (formato === "tempo") {
        return this.formatarTempo(numero);
      }

      return Math.round(numero).toLocaleString("pt-BR");
    },
    formatarTempo(horas) {
      const diasTotais = Math.floor(horas / 6);
      const meses = Math.floor(diasTotais / 22);
      const dias = diasTotais % 22;

      if (meses > 0) {
        const textoMes = `${meses} ${meses === 1 ? "mês" : "meses"}`;
        return dias > 0
          ? `${textoMes} e ${dias} ${dias === 1 ? "dia" : "dias"}`
          : textoMes;
      }

      if (diasTotais > 0) {
        return `${diasTotais} ${diasTotais === 1 ? "dia" : "dias"}`;
      }

      const horasInteiras = Math.round(horas);
      return `${horasInteiras} ${horasInteiras === 1 ? "hora" : "horas"}`;
    },
    formatarDelta(deltaPct) {
      if (deltaPct === null || typeof deltaPct === "undefined") return "—";
      return `${Math.abs(Number(deltaPct)).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}%`;
    },
    classeDelta(deltaPct) {
      const delta = Number(deltaPct);
      if (delta > 0) return "valora-delta--positive";
      if (delta < 0) return "valora-delta--negative";
      return "valora-delta--neutral";
    },
    iconeDelta(deltaPct) {
      const delta = Number(deltaPct);
      if (delta > 0) return "mdi mdi-arrow-up";
      if (delta < 0) return "mdi mdi-arrow-down";
      return "mdi mdi-minus";
    }
  }
};
</script>

<style lang="scss" scoped>
.valora-cards-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.valora-highlight-card {
  min-width: 0;
  min-height: 104px;
  transition: 0.3s ease;

  &:hover {
    box-shadow: rgba(100, 100, 111, 0.2) 0 7px 29px 0;
  }
}

.valora-highlight-card > .card-body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem !important;
}

.valora-highlight-card__main {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.65rem;
}

.valora-highlight-card__icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 1.55rem;
}

.valora-highlight-card__content {
  min-width: 0;
}

.valora-highlight-card__label {
  display: block;
  overflow: hidden;
  color: #434658;
  font-size: 0.8rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.valora-highlight-card__value {
  display: block;
  margin-top: 0.2rem;
  overflow: hidden;
  color: #2b2640;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.valora-highlight-card__footer {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.valora-delta {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 0.12rem 0.42rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.valora-delta--positive {
  color: #07985b;
  background: #eaf8f1;
}

.valora-delta--negative {
  color: #d92d3a;
  background: #fdecee;
}

.valora-delta--neutral,
.valora-delta--loading {
  color: #77717f;
  background: #f1eff4;
}

.valora-highlight-card__comparison {
  overflow: hidden;
  color: #7c7c8a;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.valora-highlight-card__skeleton {
  display: block;
  width: 80%;
  height: 1.5rem;
  margin-top: 0.35rem;
  border-radius: 5px;
  background: linear-gradient(90deg, #f1eff4 25%, #faf9fb 50%, #f1eff4 75%);
  background-size: 200% 100%;
  animation: valora-card-loading 1.2s infinite;
}

.valora-cards-error {
  color: #a72b36;
  background: #fff7f8;

  > .card-body {
    padding: 1.25rem;
  }
}

@keyframes valora-card-loading {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (max-width: 1199.98px) {
  .valora-cards-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .valora-cards-grid {
    grid-template-columns: 1fr;
  }
}
</style>
